import { Button, MultiSelect, RadioGroup, RadioGroupItem } from '@predoc/cow';
import { Send, CircleX } from 'lucide-react';
import { usePatientRequestContext } from '../../../context/usePatientRequestContext';
import {
  StepperNavigation,
  Section,
  SectionContent,
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
  Input,
  Alert,
  AlertDescription,
} from '@predoc/cow';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { format, parseISO } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { DatePickerInput } from '@/components/DatePickerInput';
import {
  submitFormSchema,
  SubmitFormType,
  SubmitFormFieldProps,
} from './validation';
import { disableDateNeededBy } from './dateNeededBy';
import { RequestMethodSummary } from './RequestSummaryItem';
import {
  useGetUserEmailsForProfile,
  useCreatePatientWithRequest,
  useCreateRequestForPatient,
  useEditPatientRequest,
} from '../../../hooks/patientsAPI';
import { RetrievalMethod } from '@/feature/profiles/constants';
import { RequestActionFormsData, PatientRequestInput } from '../../../types';
import { useDebounce } from '@/hooks';
import { useGetOrgProfileById } from '@/feature/profiles/hooks/profilesAPI';
import {
  mapPatientRequestStateToApi,
  mapPatientRequestStateToEditPatientRequestInput,
} from '../../../utils/mapRequestToApi';
import { isHIEOnlyrequest } from '@/feature/patients/utils/requestMetadataVisibility';

const REQUEST_REASON_OPTIONS = [
  { value: 'upcoming-appointment', label: 'Upcoming Appointment' },
  { value: 'information-gathering', label: 'Information Gathering' },
] as const;

const RequestSummarySection = ({
  selectedRequestMethods,
  actionFormsData,
}: {
  selectedRequestMethods: RetrievalMethod[];
  actionFormsData: RequestActionFormsData;
}) => {
  if (selectedRequestMethods.length === 0) {
    return (
      <Alert className="border-muted bg-gray-100">
        <AlertDescription>No request methods selected.</AlertDescription>
      </Alert>
    );
  }

  return (
    <Alert className="border-muted bg-gray-100">
      <AlertDescription>
        <div className="w-full space-y-3">
          <RequestMethodSummary
            selectedMethods={selectedRequestMethods}
            actionFormsData={actionFormsData}
          />
        </div>
      </AlertDescription>
    </Alert>
  );
};

export const SubmitSection = () => {
  const dateNeededByReferenceDate = new Date();
  const navigate = useNavigate();
  const {
    currentStepIndex,
    setCurrentStep,
    requestState,
    setRequestState,
    isEditMode,
    patientRequestId,
    patientId,
  } = usePatientRequestContext();
  const organizationProfileId = requestState.organizationProfileId;
  const { data: organizationProfile } = useGetOrgProfileById(
    organizationProfileId,
  );

  const [emailSearchTerm, setEmailSearchTerm] = useState<string>('');
  const debouncedEmailSearchTerm = useDebounce(emailSearchTerm, 300);

  const { emailOptions } = useGetUserEmailsForProfile(
    organizationProfileId,
    debouncedEmailSearchTerm,
  );

  // Both hooks can be called - we'll use the appropriate one based on patient.id
  const createPatientWithRequest = useCreatePatientWithRequest();
  const createRequestForPatient = useCreateRequestForPatient();
  const editPatientRequest = useEditPatientRequest();

  const isSubmitting =
    createPatientWithRequest.isPending ||
    createRequestForPatient.isPending ||
    editPatientRequest.isPending;

  const handleRedirectToList = () => {
    navigate('/patients');
  };

  const form = useForm<SubmitFormType>({
    resolver: zodResolver(submitFormSchema),
    defaultValues: requestState.submit || {
      notificationEmails: [''],
      referenceId: undefined,
      requestReason: 'upcoming-appointment',
      dateNeededBy: undefined,
    },
  });

  // Watch the request reason to conditionally show/hide date field
  const watchedRequestReason = form.watch('requestReason');

  // Specifically for edit mode, since the data will be loaded after the form is created
  useEffect(() => {
    if (requestState.submit) {
      form.reset(requestState.submit);
    }
  }, [requestState.submit, form]);

  useEffect(() => {
    if (watchedRequestReason === 'information-gathering') {
      form.setValue('dateNeededBy', undefined);
    }
  }, [watchedRequestReason, form]);

  const selectedRequestMethods =
    requestState.request?.selectedRequestMethods || [];
  const actionFormsData = requestState.request?.actionFormsData || {};
  const shouldHideSubmitMetadata = useMemo(
    () =>
      isHIEOnlyrequest({
        selectedMethods: selectedRequestMethods,
        selectedFacesheets:
          actionFormsData[RetrievalMethod.FACESHEETS]?.selectedFacesheets,
        availableFacesheets: organizationProfile?.facesheets,
      }),
    [actionFormsData, organizationProfile?.facesheets, selectedRequestMethods],
  );

  useEffect(() => {
    if (!shouldHideSubmitMetadata) {
      return;
    }

    form.setValue('requestReason', 'information-gathering');
    form.setValue('dateNeededBy', undefined);
  }, [form, shouldHideSubmitMetadata]);

  const handlePrevious = () => {
    setCurrentStep(currentStepIndex - 1);
  };

  const handleSubmit = form.handleSubmit((data) => {
    const normalizedSubmitData = shouldHideSubmitMetadata
      ? {
          ...data,
          requestReason: 'information-gathering' as const,
          dateNeededBy: undefined,
        }
      : data;

    const updatedState = {
      ...requestState,
      submit: normalizedSubmitData,
    };

    setRequestState(updatedState);

    if (isEditMode) {
      if (!patientRequestId || !patientId) return;

      const mapped = mapPatientRequestStateToEditPatientRequestInput({
        requestState: updatedState,
        crrId: patientRequestId,
        patientId,
      });
      editPatientRequest.mutate(
        mapped as Parameters<typeof editPatientRequest.mutate>[0],
        { onSuccess: handleRedirectToList },
      );
      return;
    }

    const isExistingPatient = !!requestState.patient.id;
    const mappedRequestData = mapPatientRequestStateToApi(
      updatedState,
    ) as PatientRequestInput;

    // Call the appropriate mutation based on whether patient.id exists
    if (isExistingPatient) {
      createRequestForPatient.mutate(
        {
          patientId: requestState.patient.id,
          request: mappedRequestData,
        } as Parameters<typeof createRequestForPatient.mutate>[0],
        { onSuccess: handleRedirectToList },
      );
    } else {
      createPatientWithRequest.mutate(
        {
          patient: requestState.patient,
          organizationProfileId: requestState.organizationProfileId,
          request: mappedRequestData,
        } as Parameters<typeof createPatientWithRequest.mutate>[0],
        { onSuccess: handleRedirectToList },
      );
    }
  });

  return (
    <>
      <Section>
        <SectionContent>
          <Form {...form}>
            <form className="w-full space-y-6" noValidate>
              <div className="grid grid-cols-12 items-start gap-5">
                <FormField
                  control={form.control}
                  name="notificationEmails"
                  render={({
                    field,
                  }: SubmitFormFieldProps<'notificationEmails'>) => (
                    <FormItem className="col-span-6">
                      <FormLabel>
                        Email Address(es) for status updates *
                      </FormLabel>
                      <FormControl>
                        <MultiSelect
                          options={emailOptions}
                          values={field.value}
                          handleChange={field.onChange}
                          onSearchChange={(query: string) => {
                            // Trigger API search when user types (3+ chars requirement handled in hook)
                            setEmailSearchTerm(query);
                          }}
                          placeholder="Select or search emails..."
                          searchPlaceholder="Search emails..."
                          className="w-full"
                          hideRemoveOnLastChip
                          chipPosition="below"
                          maxVisible={5}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="referenceId"
                  render={({ field }: SubmitFormFieldProps<'referenceId'>) => (
                    <FormItem className="col-span-6">
                      <FormLabel>Request Reference ID (optional)</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="text"
                          placeholder="ex. 01234567890"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-12 items-start gap-5">
                {!shouldHideSubmitMetadata && (
                  <FormField
                    control={form.control}
                    name="requestReason"
                    render={({
                      field,
                    }: SubmitFormFieldProps<'requestReason'>) => (
                      <FormItem className="col-span-6">
                        <FormLabel>Request reason *</FormLabel>
                        <FormControl>
                          <RadioGroup
                            onValueChange={field.onChange}
                            value={field.value}
                            className="flex flex-col space-y-2"
                          >
                            {REQUEST_REASON_OPTIONS.map((option) => (
                              <div
                                key={option.value}
                                className="flex items-center space-x-2"
                              >
                                <RadioGroupItem
                                  value={option.value}
                                  id={option.value}
                                />
                                <label
                                  htmlFor={option.value}
                                  className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                                >
                                  {option.label}
                                </label>
                              </div>
                            ))}
                          </RadioGroup>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                )}

                {!shouldHideSubmitMetadata &&
                  watchedRequestReason === 'upcoming-appointment' && (
                    <FormField
                      control={form.control}
                      name="dateNeededBy"
                      render={({
                        field,
                      }: SubmitFormFieldProps<'dateNeededBy'>) => (
                        <FormItem className="col-span-6">
                          <FormLabel>Date needed by *</FormLabel>
                          <FormControl>
                            <DatePickerInput
                              onChange={(date: Date | undefined) => {
                                field.onChange(
                                  date ? format(date, 'yyyy-MM-dd') : '',
                                );
                              }}
                              value={
                                field.value ? parseISO(field.value) : undefined
                              }
                              placeholder="MM/DD/YYYY"
                              disableDates={(date) =>
                                disableDateNeededBy(
                                  date,
                                  dateNeededByReferenceDate,
                                )
                              }
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
              </div>

              <div className="space-y-2">
                <div className="text-sm font-medium">Request Summary</div>
                <RequestSummarySection
                  selectedRequestMethods={selectedRequestMethods}
                  actionFormsData={actionFormsData}
                />
              </div>

              <div className="flex flex-row justify-center align-middle">
                <Button
                  size="lg"
                  className="px-8 py-2"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  loading={isSubmitting}
                >
                  Submit
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </form>
          </Form>
        </SectionContent>
      </Section>
      <StepperNavigation>
        <Button variant="outline" onClick={handlePrevious}>
          Previous
        </Button>
        <div className="ml-auto flex gap-2">
          {form.formState.isSubmitted &&
            Object.keys(form.formState.errors).length > 0 && (
              <div className="text-destructive flex items-center gap-2">
                <CircleX className="h-4 w-4" />
                Please fix validation errors.
              </div>
            )}
        </div>
      </StepperNavigation>
    </>
  );
};
